import React, { useState, useCallback, useRef } from 'react';
import {
  SaveButton,
  CREATE,
  UPDATE,
  Edit,
  Create,
  useRefresh,
  useNotify
} from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { FormSpy } from 'react-final-form';
import { withStyles } from '@material-ui/core/styles';

import dataProvider from '../../../core/providers/IccUserDataProvider';
import WireInstruction from '../../../core/apis/WireInstructionApi';
import WireInstructionSummary from './WireInstructionSummary';
import DialogTitleWithCloseButton from '../../../core/components/DialogTitleWithCloseButton';
import SimpleFormWithSave from '../../../core/components/form/SimpleFormWithSave';
import useNotifyServerError from '../../../core/hooks/useNotifyServerError';
import { useSelector } from 'react-redux';
import Confirm from '../../../core/components/forked/layout/Confirm';

const styles = () => ({
  fullWidth: {
    width: '100%'
  },
  leftButtons: {
    display: 'flex',
    flex: 'auto'
  }
});

const WireInstructionCreateDialog = props => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [dirty, setDirty] = useState(false);
  const notifyServerError = useNotifyServerError();
  const wireInstructionFormProps = useRef({});

  const {
    handleDialogToggle,
    investorId,
    classes,
    operation,
    popupTitle,
    okButtonLabel,
    hasDeleteWireInstructionPermission,
    hasCreateWireInstructionPermission,
    ...operationProps
  } = props;
  const setWireInstructionFormProps = useCallback(formProps => {
    wireInstructionFormProps.current = formProps;
  }, []);
  const fundsRelated = useSelector(
    state => state.admin.resources.fundinvestors.data
  );
  const wireinstructions = useSelector(
    state => state.admin.resources.wireinstructions
  );

  const handleSaveClick = useCallback(() => {
    // Trigger a submit of our custom quick create/update form
    // This is needed because our modal action buttons are oustide the form
    wireInstructionFormProps.current.form.submit();
  }, []);

  const checkIfExistOtherFundsToCopyWireInstructionTo = () => {
    return Object.values(fundsRelated).some(
      item => item.fundId !== operationProps.record.fundId
    );
  };

  const copyWiresInstructionsToAllFunds = async () => {
    const multipleWiToCreate = [];
    Object.values(fundsRelated).forEach(item => {
      if (item.fundId !== operationProps.record.fundId) {
        multipleWiToCreate.push({
          ...operationProps.record,
          fundId: item.fundId,
          id: null
        });
      }
    });
    const deleteWireinstructionsArray = Object.values(
      wireinstructions.data
    ).filter(item => item.fundId !== operationProps.record.fundId);
    const idsToDelete = [];
    deleteWireinstructionsArray.forEach(item => {
      if (wireinstructions.list.ids.includes(item.id)) {
        idsToDelete.push(item.id);
      }
    });

    try {
      if (idsToDelete.length > 0) {
        await dataProvider('DELETE_MANY', 'wireinstructions', {
          ids: idsToDelete
        });
      }
      await WireInstruction.createMultipleWireInstructions(multipleWiToCreate);
      refresh();
      handleDialogToggle();
      notify('ra.notification.copy_wire_instructions');
    } catch (error) {
      notifyServerError(error);
    }
  };
  const handleClose = useCallback(() => {
    if (submitting) return;
    wireInstructionFormProps.current.form.reset();
    handleDialogToggle();
  }, [handleDialogToggle, submitting]);

  const handleSubmit = useCallback(
    async values => {
      setSubmitting(true);

      // As we want to know when the new investor has been created in order to close the modal, we use the
      // dataProvider directly
      const crudOperation = operation === 'Create' ? CREATE : UPDATE;

      try {
        await dataProvider(crudOperation, 'wireinstructions', {
          data: {
            ...values,
            entityId: investorId
          }
        });
        refresh();
        handleDialogToggle();
        notify(
          operation === 'Create'
            ? 'ra.notification.created'
            : 'ra.notification.custom_updated_message'
        );
        setSubmitting(false);
      } catch (error) {
        notifyServerError(error);
        setSubmitting(false);
      }
    },
    [
      handleDialogToggle,
      investorId,
      notify,
      notifyServerError,
      operation,
      refresh
    ]
  );
  return (
    <Dialog
      fullWidth
      open={true}
      aria-label={`${operation} Wire Instruction`}
      maxWidth={false}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return null;
        handleClose();
      }}
    >
      <DialogTitleWithCloseButton onClose={handleClose}>
        {popupTitle}
      </DialogTitleWithCloseButton>
      <DialogContent>
        {operation === 'Create' && (
          <Create {...operationProps} className={classes.fullWidth}>
            <SimpleFormWithSave
              toolbar={null}
              resource="wireinstructions"
              onSave={handleSubmit}
              variant="standard"
              margin="normal"
            >
              <FormSpy>
                {props => {
                  setWireInstructionFormProps(props);
                  return (
                    <WireInstructionSummary creation investorId={investorId} />
                  );
                }}
              </FormSpy>
            </SimpleFormWithSave>
          </Create>
        )}
        {operation === 'Update' && (
          <Edit {...operationProps} className={classes.fullWidth}>
            <SimpleFormWithSave
              toolbar={null}
              resource="wireinstructions"
              onSave={handleSubmit}
              variant="standard"
              margin="normal"
            >
              <FormSpy>
                {props => {
                  setWireInstructionFormProps(props);
                  setDirty(props.dirty);
                  return <WireInstructionSummary investorId={investorId} />;
                }}
              </FormSpy>
            </SimpleFormWithSave>
          </Edit>
        )}
      </DialogContent>
      <DialogActions>
        {operation === 'Update' &&
          hasDeleteWireInstructionPermission &&
          hasCreateWireInstructionPermission &&
          checkIfExistOtherFundsToCopyWireInstructionTo() && (
            <div className={classes.leftButtons}>
              <Button
                onClick={() => setConfirmDialog(true)}
                variant="outlined"
                color="primary"
                disabled={dirty}
                disableFocusRipple
              >
                Copy wire instruction to all funds
              </Button>
            </div>
          )}
        <Confirm
          isOpen={confirmDialog}
          title="Confirmation"
          content="ra.message.confirm_copy"
          onConfirm={copyWiresInstructionsToAllFunds}
          onClose={() => setConfirmDialog(false)}
          confirm="ra.action.confirm"
        />
        <SaveButton
          style={{ minWidth: '82px' }}
          saving={submitting}
          handleSubmitWithRedirect={handleSaveClick}
          label={okButtonLabel}
          icon={<></>}
        />
        <Button
          onClick={handleClose}
          variant="outlined"
          color="primary"
          disabled={submitting}
          disableFocusRipple
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default withStyles(styles)(WireInstructionCreateDialog);
